import { AppSyncFarm, AppSyncFarmToken } from 'common/models/farm';
import { Nullable } from 'common/models/optional';
import fetch from 'cross-fetch';
import {
  AppSyncToken,
  FarmUserInfo,
  FarmUserInfoToken,
} from 'common/models/token';
import { Treasury } from 'common/models/treasury';
import {
  AppSyncUserInfo,
  IchiStats,
  UserTokenStats,
} from 'common/models/userInfo';
import {
  ChainId,
  ICHI_LEGACY_TOKENS,
  ICHI_NEW_TOKENS,
  USDC_TOKENS,
  DAI_TOKENS,
  Farm,
  XICHI_TOKENS,
} from 'constants/constants';
import { NumberUtils } from './numberUtils';
import {
  FARM_CONTRACTS,
  FarmContract,
  ICHI_CONTRACTS,
  ICHI_LEGACY_CONTRACTS,
  ICHI_STAKING_CONTRACTS,
  IchiContract,
} from 'constants/contracts';
import { ichi } from 'constants/abis/ichi';
import { ichiV2 } from 'constants/abis/ichiV2';
import { ichiStaking } from 'constants/abis/ichiStaking';

export namespace TokenUtils {
  type TokenPriceResponse = {
    usd: number;
    usd_24h_change: number;
  };

  type TokenPriceJson = Record<string, TokenPriceResponse>;

  type LookupIchiContractsResponse = {
    legacyIchiContainer: IchiContract<typeof ichi>;
    ichiV2Container: IchiContract<typeof ichiV2>;
  };

  export function getTokenByName(
    userInfo: AppSyncUserInfo,
    token: string
  ): UserTokenStats {
    if (userInfo?.tokens) {
      const finalToken = userInfo.tokens.find(
        (tk) => tk.name.toLowerCase() === token.toLowerCase()
      );
      if (finalToken) {
        return {
          address: finalToken.address,
          balance: NumberUtils.BNtoNumberWithoutDecimals(
            finalToken.balance,
            finalToken.decimals
          ),
        };
      }
    }
    return {
      balance: 0,
    };
  }

  export function getIchiStats(
    ichi: AppSyncToken,
    xichi: AppSyncToken
  ): IchiStats {
    if (ichi && xichi && ichi.name !== '' && xichi.name !== '') {
      return {
        ichiPrice: ichi.price,
        ichiPriceChange: ichi.price_24h_change,
        xichiPrice: xichi.price,
        ratio: xichi.price / ichi.price,
      };
    }
    return {
      ichiPrice: 0,
      ichiPriceChange: 0,
      xichiPrice: 0,
      ratio: 1,
    };
  }

  export function getFarmUserInfo(
    farm: AppSyncFarm,
    userInfo: AppSyncUserInfo
  ) {
    const info: FarmUserInfo = {
      availableLP: '0',
      availableLPUSD: 0,
      ichiRewards: '0',
      ichiRewardsUSD: 0,
      stakedLP: '0',
      stakedLPUSD: 0,
      totalLP: '0',
      totalLPUSD: 0,
      tokens: [],
    };
    if (!farm) {
      return info;
    }

    if (!userInfo || !userInfo || !userInfo.farms || !farm || farm.tvl === 0) {
      if (farm.tokens.length > 0) {
        let token: FarmUserInfoToken = {
          name: farm?.tokens[0].name,
          displayName: farm?.tokens[0].displayName,
          amountUSD: 0,
          amount: '0',
          totalAmountUSD: 0,
          address: farm?.tokens[0].address,
          totalAmount: '0',
        };
        info.tokens.push(token);

        if (farm.tokens.length > 1) {
          token = {
            name: farm?.tokens[1].name,
            displayName: farm?.tokens[1].displayName,
            amountUSD: 0,
            amount: '0',
            totalAmountUSD: 0,
            address: farm?.tokens[1].address,
            totalAmount: '0',
          };
          info.tokens.push(token);
        }
      }
      return info;
    }

    const poolId = farm.poolId;
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < userInfo.farms.length; i++) {
      if (userInfo.farms[i].poolId === poolId) {
        let decimals = 18;
        if (poolId === 10003) {
          // ichi-bnt pool, ichis are LPs
          decimals = 9;
        }
        // availble  LP
        info.availableLP = NumberUtils.addDecimalPointToString(
          userInfo.farms[i].availableLP,
          decimals
        );

        let lpPrice = 0;
        if (poolId === 10003) {
          const ichiToken = userInfo.tokens.find((c) => c.name === 'ichi');
          lpPrice = ichiToken ? Number(ichiToken.price) : 0;
        } else {
          // eslint-disable-next-line no-lonely-if
          if (Number(farm.totalPoolLP) > 0) {
            // eslint-disable-next-line no-mixed-operators
            lpPrice =
              (Number(farm.tvl) * 10 ** decimals) / Number(farm.totalPoolLP);
          }
        }
        info.availableLPUSD = Number(info.availableLP) * lpPrice;

        // farm rewards
        let rewardTokenName = 'ichi';
        const rewardTokenNameV2 = 'ichi_v2';
        let rewardTokenDecimals = 9;
        // temp workaround for reward decimals on polygon. TODO: do it right
        if (poolId >= 4000 && poolId < 5000) {
          rewardTokenDecimals = 18;
        }
        if (farm.isGeneric) {
          rewardTokenName = farm.farm.farmRewardTokenName.toLowerCase();
        }
        // TODO: replace this workaround with proper handling if ICHI V2 token coming from userInfo API
        const rewardToken = userInfo.tokens.find(
          (c) => c.name === rewardTokenName || c.name === rewardTokenNameV2
        );
        const rewardPrice = rewardToken ? Number(rewardToken?.price) : 0;
        if (rewardToken) {
          rewardTokenDecimals = rewardToken.decimals;
        }
        info.ichiRewards = NumberUtils.addDecimalPointToString(
          userInfo.farms[i].rewardLP,
          rewardTokenDecimals
        );
        info.ichiRewardsUSD = Number(info.ichiRewards) * rewardPrice;

        // staked LP
        const stakedLP = NumberUtils.addDecimalPointToString(
          userInfo.farms[i].userLP,
          decimals
        );
        info.stakedLP = stakedLP;
        info.stakedLPUSD = Number(stakedLP) * lpPrice;

        // total LP
        const totalStrLP =
          BigInt(userInfo.farms[i].userLP) +
          BigInt(userInfo.farms[i].availableLP).toString();
        info.totalLP = NumberUtils.addDecimalPointToString(
          totalStrLP,
          decimals
        );
        info.totalLPUSD = info.stakedLPUSD + info.availableLPUSD;

        // total LP ownership in the pool/vault
        const totalLPOwnership = info.totalLPUSD / farm?.tvl;

        // farm ratio
        const farmRatio = farm?.farmTVL / farm?.tvl;

        // tokens
        if (farm?.tokens[0]) {
          const amount =
            farm?.tokens[0].reserve * userInfo.farms[i].lpOwnership * farmRatio;
          const totalAmount = farm?.tokens[0].reserve * totalLPOwnership;
          const token: FarmUserInfoToken = {
            name: farm?.tokens[0].name,
            displayName: farm?.tokens[0].displayName,
            amountUSD: amount * farm?.tokens[0].price,
            amount: NumberUtils.threeDecimals(amount),
            totalAmountUSD: totalAmount * farm?.tokens[0].price,
            totalAmount: NumberUtils.threeDecimals(totalAmount),
            address: farm?.tokens[0].address,
          };
          info.tokens.push(token);
        }
        if (farm?.tokens[1]) {
          const amount =
            farm?.tokens[1].reserve * userInfo.farms[i].lpOwnership * farmRatio;
          const totalAmount = farm?.tokens[1].reserve * totalLPOwnership;
          const token: FarmUserInfoToken = {
            name: farm?.tokens[1].name,
            displayName: farm?.tokens[1].displayName,
            amountUSD: amount * farm?.tokens[1].price,
            amount: NumberUtils.threeDecimals(amount),
            totalAmountUSD: totalAmount * farm?.tokens[1].price,
            totalAmount: NumberUtils.threeDecimals(totalAmount),
            address: farm?.tokens[1].address,
          };
          info.tokens.push(token);
        }

        break;
      }
    }

    // add tokens in case the farm came from another network - this is for display only
    if (info.tokens.length === 0 && farm?.tokens[0]) {
      // tokens
      if (farm?.tokens[0]) {
        const token: FarmUserInfoToken = {
          name: farm?.tokens[0].name,
          displayName: farm?.tokens[0].displayName,
          amountUSD: 0,
          amount: NumberUtils.threeDecimals(0),
          totalAmountUSD: 0,
          totalAmount: '0',
        };
        info.tokens.push(token);
      }
      if (farm?.tokens[1]) {
        const token: FarmUserInfoToken = {
          name: farm?.tokens[1].name,
          displayName: farm?.tokens[1].displayName,
          amountUSD: 0,
          amount: NumberUtils.threeDecimals(0),
          totalAmountUSD: 0,
          totalAmount: '0',
        };
        info.tokens.push(token);
      }
    }

    return info;
  }

  /**
   * Check if a token is a stable coin
   */
  export function isStableCoin(
    chainId: ChainId,
    token: AppSyncFarmToken
  ): boolean {
    if (!token) {
      console.warn('Could not determine if the token is a stable coin');
      return false;
    }

    return (
      token.isOneToken ||
      [
        USDC_TOKENS[chainId]?.address.toLowerCase(),
        DAI_TOKENS[chainId]?.address.toLowerCase(),
      ].includes(token.address.toLowerCase())
    );
  }

  /**
   * Check if a tokenAddress is either a legacy or new ichi
   */
  export function isIchiToken(chainId: ChainId, tokenAddress?: string) {
    if (!tokenAddress) {
      console.warn(
        'Could not determine if the token address is an ICHI token, no address given!'
      );
      return false;
    }

    if (
      [
        ICHI_NEW_TOKENS[chainId].address.toLowerCase(),
        ICHI_LEGACY_TOKENS[chainId]?.address.toLowerCase(),
      ].includes(tokenAddress.toLowerCase())
    ) {
      return true;
    }
    return false;
  }

  export const getTreasuryByToken = (
    treasuries: Treasury[],
    token?: string
  ): Nullable<Treasury> => {
    return token
      ? treasuries.find((t) => t.name.toLowerCase() === token.toLowerCase()) ||
          treasuries[0]
      : null;
  };

  export const lookupTokenPrice = async (
    tokenAddress: string
  ): Promise<number> => {
    const response = await fetch(
      `https://api.coingecko.com/api/v3/simple/token_price/ethereum?contract_addresses=${tokenAddress}&vs_currencies=usd&include_24hr_change=true`
    );
    const json: TokenPriceJson = await response.json();
    if (json && json[tokenAddress]) {
      return json[tokenAddress].usd;
    }
    return 0;
  };

  export function lookupFarmContract(
    chainId: ChainId,
    farm: Farm
  ): Nullable<FarmContract> {
    if (!farm) {
      console.warn('Could not lookup farm contract, no farm given!');
      return null;
    }

    if (!chainId) {
      console.warn('Could not lookup farm contract, no chainId given!');
      return null;
    }
    if (!farm.address) {
      console.warn('Could not lookup farm contract, no contractAddress given!');
      return null;
    }

    const contract = FARM_CONTRACTS[chainId][farm.address];
    return contract;
  }

  export function lookupIchiContracts(
    chainId: ChainId
  ): Nullable<LookupIchiContractsResponse> {
    if (!chainId) {
      return null;
    }
    return {
      legacyIchiContainer:
        ICHI_LEGACY_CONTRACTS[chainId][ICHI_LEGACY_TOKENS[chainId].address],
      ichiV2Container:
        ICHI_CONTRACTS[chainId][ICHI_NEW_TOKENS[chainId].address],
    };
  }

  export function lookupIchiStakingContracts(
    chainId: ChainId
  ): Nullable<IchiContract<typeof ichiStaking>> {
    if (!chainId) {
      return null;
    }
    return ICHI_STAKING_CONTRACTS[chainId][XICHI_TOKENS[chainId].address];
  }
}
